import React, { useEffect, useMemo, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { COUNTRIES, DEFAULT_DIAL, flagOf } from "./countries";
import "./PhoneField.css";

// The stored value keeps the same shape the rest of the app expects:
// "(+964) 771 341 07 76"
function splitValue(value) {
  const m = /^\(\+(\d{1,4})\)\s?(.*)$/.exec(value || "");
  if (m) return { dial: m[1], local: m[2] };
  return { dial: DEFAULT_DIAL, local: value || "" };
}

const PhoneField = ({ name = "phoneNumber", value, onChange, error, disabled }) => {
  const { dial, local } = splitValue(value);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const country = useMemo(
    () => COUNTRIES.find((c) => c.dial === dial) || COUNTRIES[0],
    [dial]
  );

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    const t = setTimeout(() => searchRef.current && searchRef.current.focus(), 50);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      clearTimeout(t);
    };
  }, [open]);

  const emit = (newDial, newLocal) => {
    onChange({ target: { name, value: `(+${newDial}) ${newLocal}`, type: "text" } });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    const digits = q.replace(/[^\d]/g, "");
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || (digits && c.dial.startsWith(digits))
    );
  }, [search]);

  const selectCountry = (c) => {
    emit(c.dial, local);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="input-group mb-3 position-relative" dir="ltr" ref={wrapRef}>
      <button
        type="button"
        className="btn btn-outline-secondary dropdown-toggle pf-toggle"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-label={`Country code: ${country.name} +${country.dial}`}
      >
        <span className="pf-flag">{flagOf(country.iso)}</span> +{country.dial}
      </button>

      <InputMask
        mask="999 999 99 99"
        value={local}
        onChange={(e) => emit(dial, e.target.value)}
        id={name}
        name={name}
        dir="ltr"
        className={`form-control ${error ? "is-invalid" : ""}`}
        placeholder="771 341 07 76"
        disabled={disabled}
        required
      />
      {error ? <div className="invalid-feedback">{error}</div> : null}

      {open && (
        <div className="pf-menu bg-white border rounded shadow">
          <input
            ref={searchRef}
            className="form-control form-control-sm pf-search"
            placeholder="Search country or code… (e.g. 964)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="pf-list list-unstyled mb-0">
            {filtered.map((c) => (
              <li key={`${c.iso}-${c.dial}`}>
                <button
                  type="button"
                  className={`pf-item ${c.iso === country.iso ? "pf-active" : ""}`}
                  onClick={() => selectCountry(c)}
                >
                  <span className="pf-flag">{flagOf(c.iso)}</span>
                  <span className="pf-name">{c.name}</span>
                  <span className="text-muted">+{c.dial}</span>
                </button>
              </li>
            ))}
            {!filtered.length && <li className="p-2 small text-muted">No matches</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhoneField;
